import _ from 'lodash';
import React, { useState, useMemo } from 'react';
import SAlert from 'react-s-alert';
import { Button, InputText, Label } from 'design-system/Form';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import { Modal, ModalHeader, ModalBody } from 'react-scale/components/design-system/Modal';
import { makeStyles } from 'react-scale/components/design-system/styles';
import { RadioGroup, Radio } from 'react-scale/components/design-system';
import { textContainsCodenames, extractCodenamesFromText } from '@scale/shared/codenames';
import { CodenamesWarningModal } from 'react-scale/corp/components/CodenamesWarningModal';
import { useGetRandomConfig } from 'react-scale/corp/views/CorpProjectGroupManager/communication/apiClient';
// NOTE: this was all copied, pasted, and refactored from RecallSubtasksNeedsCorpPage.tsx
const PERMISSION_GROUP_ACTIONS = Object.freeze({
  name: 'Change Name',
  group: 'Change Group',
});

const QMUpdatePermissionGroupModal: React.FC<{
  projectId: string;
  isModalOpen: boolean;
  customer?: string;
  hideModal: (permissionName: string) => void;
}> = ({ projectId, isModalOpen, customer, hideModal }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState<boolean>(false);
  const [permissionGroupName, setPermissionGroupName] = useState<string>('');
  const [permissionGroupTitle, setPermissionGroupTitle] = useState<string>('');
  const [updateName, setUpdateName] = useState<string>('name');
  const [openWarningModal, setOpenWarningModal] = useState<boolean>(false);
  const [codenameUsed, setCodenameUsed] = useState<string>('');
  const { data: customerCodenamesConfig } = useGetRandomConfig({
    randomConfigName: 'customer-codenames-list',
  });
  const customerCodenames = useMemo(
    () => customerCodenamesConfig?.value ?? [],
    [customerCodenamesConfig],
  );

  const updatePermissionGroupName = async () => {
    setLoading(true);
    try {
      await apiFetch(null, `corp-api/update_permission_group_name`, {
        method: 'POST',
        body: {
          name: permissionGroupName,
          title: permissionGroupTitle,
          project: projectId,
        },
      });
      hideModal(permissionGroupName);
      setPermissionGroupName('');
      setPermissionGroupTitle('');
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
    } finally {
      setLoading(false);
    }
  };

  const resetAndCloseModal = () => {
    setPermissionGroupName('');
    setPermissionGroupTitle('');
    hideModal('');
  };

  const modifyPermissionGroup = async () => {
    setLoading(true);
    try {
      await apiFetch(null, 'corp-api/update_project_permission_group', {
        method: 'POST',
        body: {
          project: projectId,
          permissionGroupName,
          customer,
        },
      });
      SAlert.success(`New Permission Group it's being process, it could take some time to update.`);
      resetAndCloseModal();
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const inputContainsCodenames =
      textContainsCodenames(permissionGroupName, customerCodenames) ||
      textContainsCodenames(permissionGroupTitle, customerCodenames);

    if (inputContainsCodenames) {
      const codenamesInGroupName = extractCodenamesFromText(permissionGroupName, customerCodenames);
      const codenamesInGroupTitle = extractCodenamesFromText(
        permissionGroupTitle,
        customerCodenames,
      );

      setOpenWarningModal(true);
      const uniqueCodenames = [...new Set([...codenamesInGroupName, ...codenamesInGroupTitle])];
      setCodenameUsed(uniqueCodenames.join(', '));
      return;
    }

    if (updateName === 'name') {
      await updatePermissionGroupName();
    } else {
      await modifyPermissionGroup();
    }
  };

  return (
    <>
      <CodenamesWarningModal
        open={openWarningModal}
        codename={codenameUsed}
        onClose={() => setOpenWarningModal(false)}
      />
      <Modal isOpen={isModalOpen && !openWarningModal} onRequestClose={resetAndCloseModal}>
        <ModalHeader onRequestClose={resetAndCloseModal}>
          Update Permission Group {updateName === 'name' ? 'Title' : ''}
        </ModalHeader>
        <ModalBody>
          <Label>Select Action:</Label>
          <RadioGroup value={updateName} onChange={setUpdateName}>
            {_.map(PERMISSION_GROUP_ACTIONS, (v, k) => (
              <Radio key={v} value={k} label={v} />
            ))}
          </RadioGroup>
          <Label>Permission Group:</Label>
          <InputText
            value={permissionGroupTitle}
            placeholder={
              updateName === 'name' ? 'Permission Group Title...' : 'Permission Group ID or Name...'
            }
            onChange={e => {
              const title = e.target.value;
              setPermissionGroupTitle(title);
              setPermissionGroupName(title.toLowerCase().replace(/ /g, '_'));
            }}
          />
          <br />
          {permissionGroupTitle && updateName === 'name' && (
            <ul>
              <li>Your permission group will be displayed as: "{permissionGroupTitle}"</li>
              <li>Your permission group will be saved internally as: "{permissionGroupName}"</li>
            </ul>
          )}
          <Button
            primary
            type="button"
            disabled={!permissionGroupTitle || loading}
            className={classes.button}
            onClick={handleSave}
          >
            {loading ? 'Loading...' : 'Save'}
          </Button>
          <br />
        </ModalBody>
      </Modal>
    </>
  );
};

const useStyles = makeStyles({
  button: {
    marginLeft: 10,
    float: 'right',
  },
});

export default QMUpdatePermissionGroupModal;
